import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { StorageService } from '../services/storage.service';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const storageService = inject(StorageService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      let errorMessage = 'An error occurred';

      if (error.error instanceof ErrorEvent) {
        // Client-side error
        errorMessage = `Error: ${error.error.message}`;
      } else {
        // Server-side error
        switch (error.status) {
          case 401:
            // Unauthorized - redirect to appropriate login
            // No interceptar logout - dejar que el servicio lo maneje
            if (req.url.includes('/logout')) {
              errorMessage = error.error?.message || 'Logout error.';
              break;
            }
            
            errorMessage = 'Session expired. Please login again.';
            
            // Redirigir según el contexto (tenant vs owner)
            // Si es una petición a /api/tenant/, redirigir a login de tenant
            if (req.url.includes('/api/tenant/')) {
              storageService.clearEmployeeSession();
              router.navigate(['/employee/login']);
            } 
            // Si es petición a /api/owner/ o /api/admin/, redirigir a login de owner
            // PERO solo si no estamos en contexto de tenant (para evitar loops)
            else if (req.url.includes('/api/owner/') || req.url.includes('/api/admin/')) {
              // Verificar si estamos en un subdominio de tenant
              const hostname = window.location.hostname;
              const isTenantContext = hostname.includes('.localhost') || 
                                     (hostname.split('.').length >= 2 && 
                                      !['localhost', '127.0.0.1', 'www'].includes(hostname.split('.')[0]));
              
              if (!isTenantContext) {
                // Estamos en dominio central, redirigir a login de owner
                storageService.clear();
                router.navigate(['/login']);
              }
              // Si estamos en contexto tenant, no redirigir (podría ser un employee-owner sin permisos)
            }
            // Para otras rutas, redirigir a login principal
            else {
              storageService.clear();
              router.navigate(['/login']);
            }
            break;
          
          case 403:
            // Forbidden — priorizar el mensaje real del backend (límites de plan, roles protegidos, etc.)
            errorMessage = error.error?.mensaje || error.error?.message || error.error?.error
              || 'No tenés permiso para realizar esta acción.';
            // Módulo fuera del plan al ABRIR una pantalla: se explica en vez de dejarla
            // vacía (tarea 227). En un POST (crear un artículo de más) no se navega: el
            // formulario muestra el mensaje.
            if (error.error?.requiere_accion === 'upgrade_plan' && req.method === 'GET'
                && req.url.includes('/api/tenant/') && !router.url.startsWith('/app/plan-no-incluye')) {
              router.navigate(['/app/plan-no-incluye'], {
                queryParams: { modulo: error.error?.modulo ?? null, plan: error.error?.plan_actual?.nombre ?? null },
              });
            }
            break;

          case 404:
            // Not found
            errorMessage = 'Resource not found.';
            break;

          case 422:
            // Validation error
            errorMessage = error.error?.mensaje || error.error?.message || 'Validation error.';
            break;
          
          case 500:
            // Server error
            errorMessage = 'Server error. Please try again later.';
            break;
          
          default:
            errorMessage = error.error?.message || `Error Code: ${error.status}`;
        }
      }

      console.error('HTTP Error:', {
        status: error.status,
        message: errorMessage,
        error: error.error
      });

      return throwError(() => ({
        status: error.status,
        message: errorMessage,
        error: error.error
      }));
    })
  );
};
